import { type Action as Permission } from '@canvas/model/permissions';
import { PolicyManager } from '@canvas/policies';
import { PolicyManagerExternalApi } from '@canvas/policies/api';

type Subscriber = VoidFunction;

type PermissionsManagerOpts = {
  id: string;
  claims: Set<Permission>;
};

export class PermissionsManager {
  #id: string | undefined;

  #claims: Set<Permission> = new Set();
  #subscribers: Subscriber[] = [];

  #policyManager: PolicyManager = new PolicyManager();
  #policyManagerApi = new PolicyManagerExternalApi(
    this.#policyManager,
    this.triggerSubscribers.bind(this)
  );

  public constructor(opts?: PermissionsManagerOpts) {
    if (!opts) {
      return;
    }

    this.#id = opts.id;
    this.#claims = opts.claims;
  }

  get id() {
    return this.#id;
  }

  /**
   * @deprecated - only used for testing purposes. For all updates, use {@link update}
   */
  set id(id: string | undefined) {
    if (process.env.NODE_ENV !== 'test') {
      throw Error('cannot set id programatically outside of testing environments.');
    }

    this.#id = id;
  }

  get policies() {
    return this.#policyManagerApi;
  }

  get claims() {
    return this.#claims;
  }

  update(id: string | undefined, permissions: Set<Permission>) {
    this.#id = id;
    this.#claims.clear();

    for (const permission of permissions) {
      this.#claims.add(permission);
    }

    this.triggerSubscribers();
  }

  subscribe(subscriber: Subscriber) {
    this.#subscribers.push(subscriber);

    return () => this.unsubscribe(subscriber);
  }

  can(permission: Permission) {
    return this.#policyManager.can(permission, this.#claims);
  }

  cannot(permission: Permission) {
    return this.#policyManager.cannot(permission, this.#claims);
  }

  reset() {
    this.#claims.clear();
    this.triggerSubscribers();
  }

  destroy() {
    this.#claims.clear();
    this.#subscribers.length = 0;

    this.#policyManager.destroy();
    this.#policyManagerApi.destroy();
  }

  private unsubscribe(subscriber: Subscriber) {
    const idx = this.#subscribers.findIndex((s) => s === subscriber);
    if (idx === -1) {
      return;
    }

    this.#subscribers.splice(idx, 1);
  }

  private triggerSubscribers() {
    this.#subscribers.forEach((subscriber) => subscriber());
  }
}
